/**
 * Search.jsx — Page de recherche de véhicules
 *
 * Charge toutes les annonces depuis /api/products, puis filtre et trie côté client.
 * Les filtres actifs sont lus depuis l'URL (?query=&city=&startDate=…) pour permettre
 * le partage de liens et la navigation avec le bouton retour.
 *
 * Fonctionnalités clés :
 *  - Debounce 300ms sur les filtres (évite de recalculer à chaque frappe)
 *  - Recherche vocale via l'API Web Speech (SpeechRecognition)
 *    : extrait automatiquement la ville, le type, le budget de la transcription
 *  - Catalogue marques/modèles chargé dynamiquement selon le type sélectionné
 *  - Rendu grille ou liste selon layoutMode
 *  - `filterVehicles` et `sortVehicles` sont externalisés dans Search.js
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
		FiChevronDown,
		FiGrid,
		FiList,
		FiMapPin,
		FiRefreshCcw,
		FiSearch,
		FiSliders,
		FiStar,
		FiZap,
} from 'react-icons/fi';
import BottomNav from '../shared/BottomNav.jsx';
import Navbar from '../shared/Navbar.jsx';
import { filterVehicles, sortVehicles } from './Search.js';
import { getApiBaseUrl } from '../../utils/apiBaseUrl';
import { MOROCCAN_CITIES } from '../../utils/moroccanCities';
import './Search.css';

const vehicleTypeOptions = ['Tous', 'Citadine', 'Berline', 'SUV', 'Crossover', 'Monospace', 'Utilitaire', 'Pickup', 'Cabriolet', 'Luxe', 'Sportive'];
const transmissionOptions = ['Automatique', 'Manuelle'];
const fuelOptions = ['Essence', 'Diesel', 'Electrique', 'Hybride'];
const seatOptions = ['2', '4', '5', '7', '9+'];
const optionChoices = [
	'Climatisation',
	'GPS',
	'Siège bébé',
	'Kilometrage illimite',
	'Assurance incluse',
	'Livraison du vehicule partout',
	'Chauffeur'
];
const sortOptions = [
	{ value: 'relevance', label: 'Pertinence' },
	{ value: 'priceAsc', label: 'Prix ↑' },
	{ value: 'priceDesc', label: 'Prix ↓' },
	{ value: 'rating', label: 'Note' },
	{ value: 'newest', label: 'Nouveaute' }
];

const categoryToVehicleType = {
	Economique: 'Citadine',
	SUV: 'SUV',
	Familiale: 'Monospace',
	Luxe: 'Luxe',
	Utilitaire: 'Utilitaire'
};


function getInitialFilters() {
	return {
		query: '',
		city: '',
		startDate: '',
		endDate: '',
		budgetMax: '',
		vehicleType: 'all',
		brand: '',
		model: '',
		transmissions: [],
		fuels: [],
		seats: [],
		priceMin: '',
		priceMax: '',
		options: [],
		availableOnly: false
	};
}


function toggleValue(values, value) {
	return values.includes(value) ? values.filter((item) => item !== value) : [...values, value];
}

function renderStars(note) {
	return Array.from({ length: 5 }, (_, index) => (
		<span key={`${note}-${index}`} className={`ux-star ${index < Math.round(note) ? 'is-filled' : ''}`}>
			<FiStar aria-hidden="true" />
		</span>
	));
}

function createListingPlaceholder(label, index) {
	const safeLabel = String(label || 'Vehicle').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
	const colors = [
		['#0f172a', '#1d4ed8'],
		['#111827', '#0ea5e9'],
		['#1f2937', '#2563eb']
	];
	const [startColor, endColor] = colors[index % colors.length];
	const svg = `
		<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 500" role="img" aria-label="${safeLabel}">
			<defs>
				<linearGradient id="bg" x1="0" y1="0" x2="1" y2="1">
					<stop offset="0%" stop-color="${startColor}" />
					<stop offset="100%" stop-color="${endColor}" />
				</linearGradient>
			</defs>
			<rect width="800" height="500" rx="36" fill="url(#bg)" />
			<text x="50%" y="50%" text-anchor="middle" font-family="Montserrat, Arial, sans-serif" font-size="48" font-weight="700" fill="#f7fbff">${safeLabel}</text>
			<text x="50%" y="60%" text-anchor="middle" font-family="Rajdhani, Arial, sans-serif" font-size="22" letter-spacing="5" fill="rgba(255,255,255,0.78)">ULTIMATEX DRIVE</text>
		</svg>
	`;

	return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

function mapProductToVehicle(product, index = 0) {
	const galleryImages = Array.isArray(product.galleryImages) ? product.galleryImages.filter(Boolean) : [];
	const name = product.name || [product.vehicleBrand, product.vehicleModel].filter(Boolean).join(' ').trim() || 'Vehicule';

	return {
		id: product._id || product.id || `listing-${index}`,
		nom: name,
		agence: product.agencyName || 'Agence',
		ville: product.city || '',
		type: product.vehicleType || 'Vehicule',
		transmission: product.transmission || '',
		carburant: product.fuelType || '',
		places: Number(product.seats || 0),
		prixJour: Number(product.price || 0),
		disponible: product.isAvailable !== false && Number(product.stock || 0) > 0,
		note: Number(product.note || 0),
		options: Array.isArray(product.features) ? product.features : [],
		image: product.mainImageUrl || galleryImages[0] || createListingPlaceholder(name, index),
		brand: product.vehicleBrand || '',
		model: product.vehicleModel || '',
	};
}

function buildFiltersFromSearch(search) {
	const params = new URLSearchParams(search);
	const query = params.get('query') || '';
	const city = params.get('city') || '';
	const startDate = params.get('startDate') || '';
	const endDate = params.get('endDate') || '';
	const budgetMax = params.get('budgetMax') || '';
	const vehicleTypeParam = params.get('vehicleType') || 'all';
	const category = params.get('category') || '';

	return {
		...getInitialFilters(),
		query,
		city,
		startDate,
		endDate,
		budgetMax,
		vehicleType: vehicleTypeParam !== 'all' ? vehicleTypeParam : categoryToVehicleType[category] || 'all'
	};
}

// ── City autocomplete combobox ──────────────────────────────────────────────
function CityCombobox({ value, onChange }) {
	const [inputVal, setInputVal] = useState(value || '');
	const [open, setOpen] = useState(false);
	const wrapRef = useRef(null);

	const suggestions = useMemo(() => {
		const q = inputVal.trim().toLowerCase();
		if (!q) return MOROCCAN_CITIES.slice(0, 10);
		const starts = MOROCCAN_CITIES.filter(c => c.toLowerCase().startsWith(q));
		const contains = MOROCCAN_CITIES.filter(c => c.toLowerCase().includes(q) && !c.toLowerCase().startsWith(q));
		return [...starts, ...contains].slice(0, 8);
	}, [inputVal]);

	useEffect(() => { setInputVal(value || ''); }, [value]);

	useEffect(() => {
		const handler = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
		document.addEventListener('mousedown', handler);
		return () => document.removeEventListener('mousedown', handler);
	}, []);

	const select = (city) => { onChange(city); setInputVal(city); setOpen(false); };

	return (
		<div className="ux-combobox" ref={wrapRef}>
			<div className={`ux-search-input-wrap${value ? ' has-value' : ''}`}>
				<FiMapPin className="ux-search-input-icon" aria-hidden="true" />
				<input
					id="ux-search-city"
					type="text"
					placeholder="Toutes les villes"
					value={inputVal}
					autoComplete="off"
					onChange={(e) => { setInputVal(e.target.value); onChange(''); setOpen(true); }}
					onFocus={() => setOpen(true)}
					onKeyDown={(e) => {
						if (e.key === 'Escape') setOpen(false);
						if (e.key === 'Enter' && suggestions.length > 0) { e.preventDefault(); select(suggestions[0]); }
					}}
				/>
				{inputVal && (
					<button type="button" className="ux-combobox-clear" onClick={() => { select(''); setInputVal(''); }} aria-label="Effacer">×</button>
				)}
			</div>
			{open && (
				<ul className="ux-combobox-list">
					{!inputVal && (
						<li className="ux-combobox-option ux-combobox-all" onMouseDown={() => select('')}>
							<FiMapPin size={12} aria-hidden="true" /> Toutes les villes
						</li>
					)}
					{suggestions.map(city => (
						<li key={city} className={`ux-combobox-option${value === city ? ' is-selected' : ''}`} onMouseDown={() => select(city)}>
							<FiMapPin size={12} aria-hidden="true" /> {city}
						</li>
					))}
					{inputVal && suggestions.length === 0 && (
						<li className="ux-combobox-empty">Aucune ville trouvée</li>
					)}
				</ul>
			)}
		</div>
	);
}

// ── Generic bar combobox (brand / model) ───────────────────────────────────
function BarCombobox({ value, onChange, options = [], placeholder = 'Sélectionner', allLabel = 'Tous', disabled = false }) {
	const [inputVal, setInputVal] = useState(value || '');
	const [open, setOpen] = useState(false);
	const wrapRef = useRef(null);

	const suggestions = useMemo(() => {
		const q = inputVal.trim().toLowerCase();
		if (!q) return options.slice(0, 30);
		return options.filter(o => o.toLowerCase().includes(q)).slice(0, 15);
	}, [inputVal, options]);

	useEffect(() => { setInputVal(value || ''); }, [value]);

	useEffect(() => {
		const handler = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
		document.addEventListener('mousedown', handler);
		return () => document.removeEventListener('mousedown', handler);
	}, []);

	const select = (val) => { onChange(val); setInputVal(val); setOpen(false); };

	return (
		<div className="ux-combobox vxs-bar-combo" ref={wrapRef}>
			<div className={`ux-search-input-wrap${value ? ' has-value' : ''}`}>
				<FiChevronDown className="ux-search-input-icon" size={12} aria-hidden="true" />
				<input
					type="text"
					placeholder={placeholder}
					value={inputVal}
					autoComplete="off"
					disabled={disabled}
					onChange={(e) => { setInputVal(e.target.value); onChange(''); setOpen(true); }}
					onFocus={() => !disabled && setOpen(true)}
					onKeyDown={(e) => {
						if (e.key === 'Escape') setOpen(false);
						if (e.key === 'Enter' && suggestions.length > 0) { e.preventDefault(); select(suggestions[0]); }
					}}
				/>
				{inputVal && !disabled && (
					<button type="button" className="ux-combobox-clear" onClick={() => { select(''); setInputVal(''); }} aria-label="Effacer">×</button>
				)}
			</div>
			{open && !disabled && (
				<ul className="ux-combobox-list">
					{!inputVal && (
						<li className="ux-combobox-option ux-combobox-all" onMouseDown={() => select('')}>
							{allLabel}
						</li>
					)}
					{suggestions.map(opt => (
						<li key={opt} className={`ux-combobox-option${value === opt ? ' is-selected' : ''}`} onMouseDown={() => select(opt)}>
							{opt}
						</li>
					))}
					{inputVal && suggestions.length === 0 && (
						<li className="ux-combobox-empty">Aucun résultat</li>
					)}
				</ul>
			)}
		</div>
	);
}

function Search() {
	const resultsRef = useRef(null);
	const modelComboRef = useRef(null);
	const prevBrandRef = useRef('');
	const location = useLocation();
	const navigate = useNavigate();
	const apiBaseUrl = getApiBaseUrl();
	const shouldOpenFiltersByDefault = () => typeof window !== 'undefined' && window.innerWidth >= 1060;
	const [filtersOpen, setFiltersOpen] = useState(() => shouldOpenFiltersByDefault());
	const [layoutMode, setLayoutMode] = useState('grid');
	const [sortBy, setSortBy] = useState('relevance');
	const [filters, setFilters] = useState(getInitialFilters());
	const [debouncedFilters, setDebouncedFilters] = useState(getInitialFilters());
	const [isLoading, setIsLoading] = useState(false);
	const [vehicles, setVehicles] = useState([]);
	const [vehiclesLoading, setVehiclesLoading] = useState(true);
	const [vehiclesError, setVehiclesError] = useState('');
	const [catalogBrands, setCatalogBrands] = useState([]);
	const [catalogModels, setCatalogModels] = useState([]);

	// Fetch brands when category filter changes
	useEffect(() => {
		if (!filters.vehicleType || filters.vehicleType === 'all') {
			fetch(`${apiBaseUrl}/api/vehicle-catalog/brands`)
				.then(r => r.json())
				.then(data => setCatalogBrands(Array.isArray(data) ? data : []))
				.catch(() => {});
		} else {
			fetch(`${apiBaseUrl}/api/vehicle-catalog/brands?category=${encodeURIComponent(filters.vehicleType)}`)
				.then(r => r.json())
				.then(data => setCatalogBrands(Array.isArray(data) ? data : []))
				.catch(() => {});
		}
		setCatalogModels([]);
		setFilters(prev => ({ ...prev, brand: '', model: '' }));
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filters.vehicleType, apiBaseUrl]);

	// Fetch models when brand filter changes
	useEffect(() => {
		if (!filters.brand) { setCatalogModels([]); return; }
		const params = new URLSearchParams({ brand: filters.brand });
		if (filters.vehicleType && filters.vehicleType !== 'all') params.set('category', filters.vehicleType);
		fetch(`${apiBaseUrl}/api/vehicle-catalog/models?${params}`)
			.then(r => r.json())
			.then(data => setCatalogModels(Array.isArray(data) ? data.map(e => e.model) : []))
			.catch(() => {});
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filters.brand, apiBaseUrl]);

	useEffect(() => {
		const filtersFromUrl = buildFiltersFromSearch(location.search);
		setFilters(filtersFromUrl);
		setDebouncedFilters(filtersFromUrl);
		setSortBy('relevance');
		setFiltersOpen(shouldOpenFiltersByDefault());
	}, [location.search]);

	useEffect(() => {
		let active = true;

		const loadVehicles = async () => {
			setVehiclesLoading(true);
			setVehiclesError('');

			try {
				const response = await fetch(`${apiBaseUrl}/api/products`);
				const data = await response.json();

				if (!response.ok) {
					throw new Error(data?.message || 'Impossible de charger les annonces.');
				}

				if (!active) return;

				setVehicles(Array.isArray(data) ? data.map(mapProductToVehicle) : []);
			} catch (error) {
				if (!active) return;
				setVehicles([]);
				setVehiclesError(error.message || 'Impossible de charger les annonces.');
			} finally {
				if (active) {
					setVehiclesLoading(false);
				}
			}
		};

		loadVehicles();

		return () => {
			active = false;
		};
	}, [apiBaseUrl]);



	// Debounce 300ms : applique les filtres 300ms après la dernière modification
	// (évite de recalculer filteredVehicles à chaque frappe dans le champ texte)
	useEffect(() => {
		setIsLoading(true);
		const timeoutId = window.setTimeout(() => {
			setDebouncedFilters(filters);
			setIsLoading(false);
		}, 300);

		return () => {
			window.clearTimeout(timeoutId);
		};
	}, [filters]);

	const filteredVehicles = useMemo(() => {
		const preparedVehicles = filterVehicles(vehicles, debouncedFilters)
			.filter(v => !debouncedFilters.brand || (v.brand || '').toLowerCase().includes(debouncedFilters.brand.toLowerCase()))
			.filter(v => !debouncedFilters.model || (v.model || '').toLowerCase().includes(debouncedFilters.model.toLowerCase()));
		return sortVehicles(preparedVehicles, sortBy);
	}, [vehicles, debouncedFilters, sortBy]);

	const activeFiltersCount = useMemo(() => {
		let count = 0;
		if (filters.query) count += 1;
		if (filters.city) count += 1;
		if (filters.startDate) count += 1;
		if (filters.endDate) count += 1;
		if (filters.budgetMax) count += 1;
		if (filters.vehicleType && filters.vehicleType !== 'all') count += 1;
		if (filters.priceMin) count += 1;
		if (filters.priceMax) count += 1;
		if (filters.availableOnly) count += 1;
		count += filters.transmissions.length;
		count += filters.fuels.length;
		count += filters.seats.length;
		count += filters.options.length;
		return count;
	}, [filters]);

	const handleInputChange = (event) => {
		const { name, value, type, checked } = event.target;
		setFilters((previous) => ({
			...previous,
			[name]: type === 'checkbox' ? checked : value
		}));
	};

	const handleArrayChange = (key, value) => {
		setFilters((previous) => ({
			...previous,
			[key]: toggleValue(previous[key], value)
		}));
	};

	// Auto-focus model combobox input when brand is freshly selected
	useEffect(() => {
		if (filters.brand && filters.brand !== prevBrandRef.current) {
			prevBrandRef.current = filters.brand;
			const timer = setTimeout(() => {
				modelComboRef.current?.querySelector('input')?.focus();
			}, 80);
			return () => clearTimeout(timer);
		}
		if (!filters.brand) prevBrandRef.current = '';
	}, [filters.brand]);

	const handleReset = () => {
		setFilters(getInitialFilters());
		setSortBy('relevance');
		setLayoutMode('grid');
		setFiltersOpen(true);
	};

	const handleSearchSubmit = (event) => {
		event.preventDefault();
		resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
	};

	const handleAssistantClick = () => {
		navigate('/assistant-ia');
	};



	const activeType = filters.vehicleType === 'all' || !filters.vehicleType ? 'Tous' : filters.vehicleType;

	return (
		<div className="vxs-page">
			<Navbar />

			{/* ── Hero ── */}
			<section className="vxs-hero">
				<div className="vxs-hero-inner">
					<div>
						<span className="vxs-kicker"><FiZap size={12} aria-hidden="true" /> Recherche intelligente · IA disponible</span>
						<h1 className="vxs-hero-title">
							Trouvez la voiture<br /><em>parfaite</em> pour votre trajet.
						</h1>
					</div>

					{/* inline search bar */}
					<form className="vxs-bar" onSubmit={handleSearchSubmit}>
						<div className="vxs-bar-cell vxs-bar-select-cell">
							<span>Marque</span>
							<BarCombobox
								value={filters.brand}
								onChange={b => setFilters(p => ({ ...p, brand: b, model: '' }))}
								options={catalogBrands}
								placeholder="Toutes les marques"
								allLabel="Toutes les marques"
							/>
						</div>

						{filters.brand && (
							<>
								<div className="vxs-bar-sep" />
								<div className="vxs-bar-cell vxs-bar-select-cell" ref={modelComboRef}>
									<span>Modèle</span>
									<BarCombobox
										value={filters.model}
										onChange={m => setFilters(p => ({ ...p, model: m }))}
										options={catalogModels}
										placeholder="Tous les modèles"
										allLabel="Tous les modèles"
										disabled={catalogModels.length === 0}
									/>
								</div>
							</>
						)}

						<div className="vxs-bar-sep" />

						<div className="vxs-bar-cell">
							<span>Ville</span>
							<CityCombobox
								value={filters.city}
								onChange={(city) => setFilters(p => ({ ...p, city }))}
							/>
						</div>

						<div className="vxs-bar-sep" />

						<div className="vxs-bar-cell">
							<span>Départ</span>
							<input name="startDate" type="date" value={filters.startDate} onChange={handleInputChange} />
						</div>

						<div className="vxs-bar-sep" />

						<div className="vxs-bar-cell">
							<span>Retour</span>
							<input name="endDate" type="date" value={filters.endDate} onChange={handleInputChange} />
						</div>

						<div className="vxs-bar-sep" />

						<div className="vxs-bar-cell vxs-bar-budget">
							<span>Budget max</span>
							<div className="vxs-bar-budget-row">
								<input
									name="budgetMax"
									type="number"
									min="0"
									value={filters.budgetMax}
									onChange={handleInputChange}
									placeholder="Illimité"
								/>
								<span>DH</span>
							</div>
						</div>

						<button type="submit" className="vxs-bar-btn">
							<FiSearch size={17} aria-hidden="true" />
							Chercher
						</button>
					</form>

					{/* type pills + AI hint */}
					<div className="vxs-hero-bottom">
						<div className="vxs-type-row">
							{vehicleTypeOptions.map((t) => (
								<button
									key={t}
									type="button"
									className={`vxs-type-pill${activeType === t ? ' is-active' : ''}`}
									onClick={() => setFilters(p => ({ ...p, vehicleType: t === 'Tous' ? 'all' : t }))}
								>
									{t}
								</button>
							))}
						</div>
						<div className="vxs-hero-tools">
							<button type="button" className="vxs-tool-btn vxs-tool-ai" onClick={handleAssistantClick}>
								<FiZap size={13} aria-hidden="true" />
								Assistant IA
							</button>
						</div>
					</div>

					<p className="vxs-ai-hint-text">
						<FiZap size={11} aria-hidden="true" />
						Vous ne savez pas quoi choisir ? Demandez à notre assistant IA, il trouve le véhicule idéal pour vous.
					</p>
				</div>
			</section>

			{/* ── Results ── */}
			<div className="vxs-results-wrap" ref={resultsRef}>
				<div className="vxs-layout">

					{/* sidebar filters */}
					<aside className={`vxs-filters${filtersOpen ? ' is-open' : ''}`}>
						<button
							type="button"
							className="vxs-filters-toggle"
							onClick={() => setFiltersOpen(p => !p)}
							aria-expanded={filtersOpen}
						>
							<FiSliders size={15} aria-hidden="true" />
							<span>Filtres avancés</span>
							<FiChevronDown size={15} className="vxs-toggle-arrow" aria-hidden="true" />
						</button>

						<div className="vxs-filters-body">
							<div className="vxs-filter-group">
								<p className="vxs-filter-label">Marque</p>
								<div className="vxs-select-wrap">
									<select value={filters.brand} onChange={e => setFilters(p => ({ ...p, brand: e.target.value, model: '' }))} disabled={catalogBrands.length === 0}>
										<option value="">Toutes les marques</option>
										{catalogBrands.map(b => <option key={b} value={b}>{b}</option>)}
									</select>
									<FiChevronDown size={14} className="vxs-select-icon" aria-hidden="true" />
								</div>
							</div>

							<div className="vxs-filter-group">
								<p className="vxs-filter-label">Modèle</p>
								<div className={`vxs-select-wrap${!filters.brand ? ' is-disabled' : ''}`}>
									<select value={filters.model} onChange={e => setFilters(p => ({ ...p, model: e.target.value }))} disabled={!filters.brand || catalogModels.length === 0}>
										<option value="">Tous les modèles</option>
										{catalogModels.map(m => <option key={m} value={m}>{m}</option>)}
									</select>
									<FiChevronDown size={14} className="vxs-select-icon" aria-hidden="true" />
								</div>
							</div>

							<div className="vxs-filter-group">
								<p className="vxs-filter-label">Transmission</p>
								<div className="vxs-chips">
									{transmissionOptions.map(v => (
										<button key={v} type="button" className={`vxs-chip${filters.transmissions.includes(v) ? ' is-on' : ''}`} onClick={() => handleArrayChange('transmissions', v)}>{v}</button>
									))}
								</div>
							</div>

							<div className="vxs-filter-group">
								<p className="vxs-filter-label">Carburant</p>
								<div className="vxs-chips">
									{fuelOptions.map(v => (
										<button key={v} type="button" className={`vxs-chip${filters.fuels.includes(v) ? ' is-on' : ''}`} onClick={() => handleArrayChange('fuels', v)}>{v}</button>
									))}
								</div>
							</div>

							<div className="vxs-filter-group">
								<p className="vxs-filter-label">Places</p>
								<div className="vxs-chips">
									{seatOptions.map(v => (
										<button key={v} type="button" className={`vxs-chip${filters.seats.includes(v) ? ' is-on' : ''}`} onClick={() => handleArrayChange('seats', v)}>{v}</button>
									))}
								</div>
							</div>

							<div className="vxs-filter-group">
								<p className="vxs-filter-label">Prix DH/jour</p>
								<div className="vxs-range-row">
									<input name="priceMin" type="number" min="0" placeholder="0" value={filters.priceMin} onChange={handleInputChange} />
									<span>—</span>
									<input name="priceMax" type="number" min="0" placeholder="1000" value={filters.priceMax} onChange={handleInputChange} />
								</div>
							</div>

							<div className="vxs-filter-group">
								<p className="vxs-filter-label">Options</p>
								<div className="vxs-chips">
									{optionChoices.map(v => (
										<button key={v} type="button" className={`vxs-chip${filters.options.includes(v) ? ' is-on' : ''}`} onClick={() => handleArrayChange('options', v)}>{v}</button>
									))}
								</div>
							</div>

							<div className="vxs-filter-group">
								<label className="vxs-toggle-check">
									<input type="checkbox" name="availableOnly" checked={filters.availableOnly} onChange={handleInputChange} />
									Disponible immédiatement
								</label>
							</div>

							<button type="button" className="vxs-reset-btn" onClick={handleReset}>
								<FiRefreshCcw size={13} aria-hidden="true" /> Réinitialiser
							</button>
						</div>
					</aside>

					{/* results */}
					<section className="vxs-results">
						{/* toolbar */}
						<div className="vxs-toolbar">
							<div className="vxs-toolbar-left">
								<div className="vxs-count">
									<strong>{filteredVehicles.length}</strong>
									<span>véhicule{filteredVehicles.length !== 1 ? 's' : ''}</span>
								</div>
								{activeFiltersCount > 0 && (
									<span className="vxs-active-filters">{activeFiltersCount} filtre{activeFiltersCount > 1 ? 's' : ''}</span>
								)}
							</div>
							<div className="vxs-toolbar-right">
								<div className="vxs-sort">
									{sortOptions.map(o => (
										<button key={o.value} type="button" className={`vxs-sort-btn${sortBy === o.value ? ' is-active' : ''}`} onClick={() => setSortBy(o.value)}>
											{o.label}
										</button>
									))}
								</div>
								<div className="vxs-layout-btns">
									<button type="button" className={`vxs-layout-btn${layoutMode === 'grid' ? ' is-active' : ''}`} onClick={() => setLayoutMode('grid')} aria-pressed={layoutMode === 'grid'}>
										<FiGrid size={15} aria-hidden="true" />
									</button>
									<button type="button" className={`vxs-layout-btn${layoutMode === 'list' ? ' is-active' : ''}`} onClick={() => setLayoutMode('list')} aria-pressed={layoutMode === 'list'}>
										<FiList size={15} aria-hidden="true" />
									</button>
								</div>
							</div>
						</div>

						{/* cards */}
						{isLoading || vehiclesLoading ? (
							<div className={`vxs-cards-${layoutMode}`}>
								{Array.from({ length: 6 }, (_, i) => (
									<article key={i} className="vxs-card is-skeleton" aria-hidden="true">
										<div className="vxs-skel vxs-skel-img" />
										<div className="vxs-card-body">
											<div className="vxs-skel vxs-skel-title" />
											<div className="vxs-skel vxs-skel-text" />
											<div className="vxs-skel vxs-skel-text short" />
										</div>
									</article>
								))}
							</div>
						) : filteredVehicles.length ? (
							<div className={`vxs-cards-${layoutMode}`}>
								{filteredVehicles.map((vehicle) => (
									<article key={vehicle.id} className={`vxs-card${layoutMode === 'list' ? ' is-list' : ''}`}>
										<div className="vxs-card-media">
											<img src={vehicle.image} alt={vehicle.nom} className="vxs-card-img" loading="lazy" />
											<div className="vxs-card-top">
												<span className="vxs-badge-type">{vehicle.type}</span>
												<span className={`vxs-badge-avail ${vehicle.disponible ? 'avail' : 'unavail'}`}>
													{vehicle.disponible ? '● Dispo' : '● Indispo'}
												</span>
											</div>
										</div>
										<div className="vxs-card-body">
											<div className="vxs-card-row1">
												<div>
													<h2 className="vxs-card-name">{vehicle.nom}</h2>
													<p className="vxs-card-agency">{vehicle.agence}</p>
												</div>
												<div className="vxs-card-rating">
													<div className="vxs-stars" aria-label={`Note ${vehicle.note} sur 5`}>
														{renderStars(vehicle.note)}
													</div>
													<span>{vehicle.note.toFixed(1)}</span>
												</div>
											</div>
											<p className="vxs-card-location">
												<FiMapPin size={12} aria-hidden="true" /> {vehicle.ville}
											</p>
											<div className="vxs-card-specs">
												<span>{vehicle.transmission}</span>
												<span>{vehicle.places} places</span>
											</div>
											{(vehicle.options || []).length > 0 && (
												<div className="vxs-card-opts">
													{(vehicle.options || []).slice(0, 3).map(o => <span key={o}>{o}</span>)}
													{(vehicle.options || []).length > 3 && <span>+{(vehicle.options || []).length - 3}</span>}
												</div>
											)}
											<div className="vxs-card-footer">
												<div className="vxs-price-block">
													<span className="vxs-price">{vehicle.prixJour}</span>
													<span className="vxs-price-unit">DH<br />/ jour</span>
												</div>
												<Link to={`/vehicle/${vehicle.id}`} className="vxs-card-cta">
													Voir →
												</Link>
											</div>
										</div>
									</article>
								))}
							</div>
						) : (
							<div className="vxs-empty">
								<div className="vxs-empty-icon"><FiSearch size={26} aria-hidden="true" /></div>
								<h2>{vehiclesError ? 'Erreur de chargement' : 'Aucun véhicule trouvé'}</h2>
								<p>{vehiclesError || 'Modifiez vos critères ou réinitialisez les filtres.'}</p>
								<button type="button" className="vxs-reset-btn" style={{ width: 'auto', padding: '0.65rem 1.5rem' }} onClick={handleReset}>
									<FiRefreshCcw size={13} /> Réinitialiser
								</button>
							</div>
						)}
					</section>

				</div>
			</div>

			<BottomNav />
		</div>
	);
}

export default Search;
